import React from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

/**
 * Footer Component
 * Bottom section with navigation links and branding
 * Includes Privacy Policy, Contact, and About links
 * Uses React Router Links for navigation
 */
function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          {/* Brand Section */}
          <div className="footer-brand">
            {/* Small Locker Icon */}
            <div className="footer-logo">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="18" width="20" height="16" rx="2" fill="#27AE60" />
                <path d="M 13 18 L 13 13 Q 13 8, 20 8 Q 27 8, 27 13 L 27 18" stroke="#F1C40F" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="20" cy="26" r="2" fill="#1A1F36" />
              </svg>
            </div>
            
            <div className="footer-brand-text">
              <h3 className="footer-title">lockitup</h3>
              <p className="footer-tagline">Secure your device. Protect your privacy.</p>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="footer-nav">
            <h4 className="footer-nav-title">Quick Links</h4>
            <ul className="footer-links">
              <li>
                <Link to="/download" className="footer-link footer-link-download">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2V11M8 11L5 8M8 11L11 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 12V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Download
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="footer-link">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 1L2 4V7C2 10.5 4.5 13.5 8 15C11.5 13.5 14 10.5 14 7V4L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/contact" className="footer-link">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 3H14V12H2V3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M2 3L8 8L14 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/about" className="footer-link">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M8 6V11M8 4H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  About
                </Link>
              </li>
            </ul>
          </nav>

          {/* Additional Info */}
          <div className="footer-info">
            <h4 className="footer-nav-title">Support</h4>
            <p className="footer-info-text">
              Free, open-source software built for your security.
            </p>
            <div className="footer-badges">
              <span className="footer-badge">Windows 10+</span>
              <span className="footer-badge">No Ads</span>
              <span className="footer-badge">Open Source</span>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="footer-social">
            <h4 className="footer-nav-title">Connect With Us</h4>
            <p className="footer-info-text">
              Follow us for updates and support
            </p>
            <div className="footer-social-links">
              <a 
                href="https://www.instagram.com/lockitup3/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-link footer-social-instagram"
                aria-label="Follow us on Instagram"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="18" cy="6" r="1.5" fill="currentColor"/>
                </svg>
                <span>Instagram</span>
              </a>
              
              <a 
                href="https://www.youtube.com/@lockitup-r5l" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-link footer-social-youtube"
                aria-label="Subscribe to our YouTube channel"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.54 6.42C22.4212 5.94541 22.1793 5.51057 21.8387 5.15941C21.498 4.80824 21.0708 4.55318 20.6 4.42C18.88 4 12 4 12 4C12 4 5.12 4 3.4 4.46C2.92925 4.59318 2.50198 4.84824 2.16135 5.19941C1.82072 5.55057 1.57879 5.98541 1.46 6.46C1.14521 8.20556 0.991228 9.97631 1 11.75C0.988771 13.537 1.14275 15.3213 1.46 17.08C1.59092 17.5398 1.83839 17.9581 2.17823 18.2945C2.51806 18.6308 2.93884 18.8738 3.4 19C5.12 19.46 12 19.46 12 19.46C12 19.46 18.88 19.46 20.6 19C21.0708 18.8668 21.498 18.6118 21.8387 18.2606C22.1793 17.9094 22.4212 17.4746 22.54 17C22.8524 15.2676 23.0064 13.5103 23 11.75C23.0112 9.96295 22.8572 8.1787 22.54 6.42Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  <path d="M9.75 15.02L15.5 11.75L9.75 8.48V15.02Z" fill="currentColor"/>
                </svg>
                <span>YouTube</span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="footer-divider"></div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {currentYear} lockitup. All rights reserved.
          </p>
          <p className="footer-disclaimer">
            Free software for personal use. Use responsibly.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
